import { Link } from 'react-router-dom';
import { ArrowRight, Box, Check, Flower2, Grape, KeyRound, Lightbulb, Sparkles } from 'lucide-react';
import PropTypes from 'prop-types';
import { PRODUCTS } from '../../products/registry.js';
import { FadeIn, StaggerList } from '../../components/ui/index.js';
import { useAuthContext } from '../../components/providers/AuthProvider.jsx';
import { canUseCustomizer } from '../../lib/permissions.js';

const PRODUCT_ICONS = {
    keyring: KeyRound,
    lightbox: Lightbulb,
    bubble: Grape,
    flower: Flower2,
};

// Los acentos del registry se traducen a tokens semánticos de DaisyUI para que
// la página funcione igual en light y dark sin colores literales.
const ACCENTS = {
    coral: {
        eyebrow:  'text-primary',
        visual:   'from-primary/90 to-primary/60',
        featureBg:'bg-primary/10 text-primary',
        arrow:    'text-primary',
        btn:      'btn-primary',
    },
    violet: {
        eyebrow:  'text-accent',
        visual:   'from-accent/90 to-accent/60',
        featureBg:'bg-accent/10 text-accent',
        arrow:    'text-accent',
        btn:      'btn-accent',
    },
    teal: {
        eyebrow:  'text-secondary',
        visual:   'from-secondary/90 to-secondary/60',
        featureBg:'bg-secondary/10 text-secondary',
        arrow:    'text-secondary',
        btn:      'btn-secondary',
    },
};

const productEntries = Object.values(PRODUCTS).filter((product) => product.selector);

function ProductCard({ product, index }) {
    const content = product.selector;
    const ProductIcon = PRODUCT_ICONS[content.icon] || Box;
    const accent = ACCENTS[content.accent] || ACCENTS.coral;

    return (
        <article className="glassCard backdrop-blur-md rounded-2xl shadow-xl overflow-hidden h-full border border-base-300 transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2">
            <Link
                to={product.routeBase}
                className="grid grid-cols-1 sm:grid-cols-[minmax(220px,1fr)_1.15fr] h-full focus:outline-none group"
                aria-label={`${content.action}: ${content.description}`}
            >
                {/* Visual */}
                <div
                    className={`relative aspect-[4/3] sm:aspect-auto min-h-[240px] sm:min-h-[400px] grid place-items-center overflow-hidden bg-gradient-to-br ${accent.visual}`}
                    aria-hidden="true"
                >
                    <span className="absolute -top-16 -left-16 w-52 h-52 rounded-full bg-base-100/10" />
                    <span className="absolute -right-10 bottom-10 w-36 h-36 rounded-full bg-base-100/10" />
                    <span className="absolute inset-4 rounded-xl border border-base-100/25" />
                    <span className="relative z-10 w-32 h-32 md:w-40 md:h-40 grid place-items-center rounded-[2rem] border border-base-100/50 bg-base-100/20 text-primary-content shadow-lg backdrop-blur -rotate-3 transition-transform duration-300 group-hover:rotate-0 group-hover:scale-105">
                        <ProductIcon className="w-14 h-14 md:w-20 md:h-20" strokeWidth={1.4} />
                    </span>
                    <span className="absolute z-10 left-6 bottom-6 text-xs font-extrabold tracking-widest text-primary-content/80">
                        0{index + 1}
                    </span>
                </div>

                {/* Cuerpo */}
                <div className="p-6 md:p-7 flex flex-col">
                    <p className={`text-xs font-extrabold uppercase tracking-widest ${accent.eyebrow}`}>
                        {content.eyebrow}
                    </p>
                    <h2 className="mt-2 text-2xl md:text-3xl font-extrabold leading-tight dark:text-white text-gray-900">
                        {content.title}
                    </h2>
                    <p className="mt-3 text-sm md:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                        {content.description}
                    </p>

                    <ul className="mt-5 mb-6 flex flex-col gap-2" aria-label="Características">
                        {content.features.map((feature) => (
                            <li key={feature} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                <span className={`w-5 h-5 shrink-0 grid place-items-center rounded-full ${accent.featureBg}`}>
                                    <Check size={13} strokeWidth={2.5} />
                                </span>
                                {feature}
                            </li>
                        ))}
                    </ul>

                    <div className="mt-auto pt-4 border-t border-base-300 flex items-center justify-between gap-4">
                        <span className={`btn ${accent.btn} btn-sm`}>{content.action}</span>
                        <ArrowRight
                            size={18}
                            className={`${accent.arrow} transition-transform duration-200 group-hover:translate-x-1`}
                        />
                    </div>
                </div>
            </Link>
        </article>
    );
}

ProductCard.propTypes = {
    product: PropTypes.shape({
        key: PropTypes.string.isRequired,
        routeBase: PropTypes.string.isRequired,
        selector: PropTypes.shape({
            eyebrow: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            description: PropTypes.string.isRequired,
            action: PropTypes.string.isRequired,
            icon: PropTypes.string,
            accent: PropTypes.string,
            features: PropTypes.arrayOf(PropTypes.string).isRequired,
        }).isRequired,
    }).isRequired,
    index: PropTypes.number.isRequired,
};

export default function CustomizerSelector() {
    const authContext = useAuthContext();
    // Solo los que su rol puede usar (C3D-27); el backend lo vuelve a exigir.
    const availableEntries = productEntries.filter((product) => canUseCustomizer(authContext, product.key));

    return (
        <main className="h-full w-full overflow-y-auto overflow-x-hidden bg-gray-100 dark:bg-base-200 text-gray-900 dark:text-gray-100 relative">
            {/* Halos de fondo — decorativos */}
            <div
                aria-hidden="true"
                className="pointer-events-none fixed top-[12%] -left-[12%] w-[26vw] h-[26vw] min-w-[250px] min-h-[250px] rounded-full bg-primary opacity-10 blur-3xl"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none fixed bottom-[4%] -right-[10%] w-[26vw] h-[26vw] min-w-[250px] min-h-[250px] rounded-full bg-accent opacity-10 blur-3xl"
            />

            <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-20 min-h-full flex flex-col justify-center">
                <FadeIn className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
                    <span className="badge badge-outline gap-2 text-xs font-bold uppercase tracking-widest py-3">
                        <Sparkles size={14} />
                        Tu espacio creativo
                    </span>
                    <h1 className="mt-5 text-4xl md:text-6xl font-black leading-tight tracking-tight dark:text-white text-gray-900">
                        ¿Qué quieres crear hoy?
                    </h1>
                    <p className="mt-4 text-base md:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                        Elige un Customiz3r para empezar. Cada herramienta está preparada
                        para las necesidades de su producto.
                    </p>
                </FadeIn>

                {availableEntries.length > 0 ? (
                    <StaggerList
                        items={availableEntries}
                        className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6 items-stretch"
                        renderItem={(product, index) => <ProductCard product={product} index={index} />}
                    />
                ) : (
                    <p className="text-center text-base-content/70">
                        Ahora mismo no hay ningún customizador disponible para tu cuenta.
                    </p>
                )}

                <FadeIn>
                    <p className="mt-8 text-center text-xs text-gray-500 dark:text-gray-400">
                        Estamos preparando nuevos formatos. Tu colección de Customiz3rs seguirá creciendo.
                    </p>
                </FadeIn>
            </div>
        </main>
    );
}
